import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import { RootState } from 'handlers';
import { uploadPaystubs } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import FormContainer from 'components/LoanForm/FormContainer';
import Button from 'components/Button';
import DropzoneWidget, { useFiles } from 'components/DropzoneWidget';
import { getApplicationData } from 'selectors/getApplicationData';
import { StepComponent } from 'components/Steps/stepUtils';
import { getStudentLoanApplication } from 'selectors/getStudentLoanApplication';
import { StepsResult } from 'enums/FlowNextResults';
import { ReactComponent as CheckIcon } from 'images/green-check-rounded.svg';
import { usePartnerData } from 'enums/PartnerName';

import styles from './UploadIncomeVerification.module.scss';

const FilesPreview = React.lazy(() => import('components/FilesPreview'));

const renameFiles = (cb: (files: File[]) => void, filePrefix?: string) => (files: File[]) => {
  const prefix = filePrefix ? `${filePrefix} ` : '';
  return cb(files.map((file) => new File([file], `${prefix}${file.name}`, { type: file.type })));
};

const UploadIncomeVerification = ({ handleNext }: StepComponent) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { handleAddFiles, files, handleRemoveFiles } = useFiles();

  const {
    applicationId,
    applicationData: { applyProgress },
  } = useSelector(getStudentLoanApplication);

  const { isApplicationStatusUpdating } = useSelector(getApplicationData);
  const { isLoading } = useSelector((state: RootState) => state.filesUpload);

  const { downloadPayrollInstructions } = usePartnerData();

  const handleContinue = async () => {
    const filesForUpload: File[] = files.map((item) => item.file);

    analytics.track('Upload Income Verification in Progress');
    await dispatchWithUnwrap(
      uploadPaystubs({
        applicationId: applicationId!,
        files: filesForUpload,
      }),
    );

    handleNext(StepsResult.Completed);
  };

  return (
    <FormContainer
      title="Income Verification"
      subtitle="Upload either your most recent W2 or your last 2 paystubs."
      className={styles.formContainer}
    >
      {!!downloadPayrollInstructions && (
        <>
          <p>To download your W2 or paystubs:</p>
          {downloadPayrollInstructions}
        </>
      )}

      <DropzoneWidget handleFiles={renameFiles(handleAddFiles, 'Paystub')} extensions={['application/pdf']} />
      <ul className={styles.filesRequirementsList}>
        <li>Only PDF files are supported</li>
        <li>Maximum file size 10MB</li>
      </ul>
      {files.length ? (
        <Suspense fallback={<ReactLoading type="spin" color="#fff" />}>
          <FilesPreview files={files} handleRemove={handleRemoveFiles} />
        </Suspense>
      ) : (
        <></>
      )}

      {applyProgress?.incomeVerification && (
        <>
          <div className={styles.uploaded}>
            <CheckIcon className={styles.checkIcon} />
            <p className={styles.verifiedLabel}>Income documents uploaded</p>
          </div>

          {!files.length && <Button onClick={handleNext}>Next</Button>}
        </>
      )}

      {Boolean(!applyProgress?.incomeVerification || files.length) && (
        <Button
          disabled={!files.length}
          className={styles.button}
          onClick={handleContinue}
          isLoading={isLoading || isApplicationStatusUpdating}
        >
          Save and Continue
        </Button>
      )}
    </FormContainer>
  );
};

export default UploadIncomeVerification;
